import { ssoProvider } from './sso';
import { PlatformRole, type PlatformRole as PlatformRoleValue } from './roles';
import { createActivityTracker } from './sessionActivity';
import { createRenewalCoordinator } from './renewalCoordinator';
import type { AccessProvider, AccessState, Principal } from './types';

/**
 * No inactivity threshold is documented anywhere in this repository or the
 * identity provider's own configuration today (see the wave report, §4) —
 * this is an operative default, not a value read from configuration, and
 * should be revisited once the product owner sets one explicitly.
 */
const INACTIVITY_THRESHOLD_MS = 15 * 60 * 1000;
const INACTIVITY_CHECK_INTERVAL_MS = 30_000;
// A single retry after a short backoff before a failed renewal is treated as
// a genuine session end, never as the first sign of one — see "qué pasa
// cuando la renovación falla" in the wave report.
const TRANSIENT_RETRY_DELAY_MS = 5_000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Maps the semantic **client** roles the corporate identity provider issues
 * on this client (`resource_access.{client}.roles`) to this codebase's
 * internal role constants. Mapped at this one boundary, deliberately: the
 * realm-side role names (`superadmin`, `admin`, `auditor`) and this
 * repository's internal constants (`platform:superadmin`, `platform:admin`,
 * `platform:auditor`, see `roles.ts`) are two separate vocabularies that must
 * never be renamed into each other.
 *
 * One client role may expand to several internal ones, which is why the values
 * are arrays: `superadmin` carries `platform:admin` as well, because the
 * expansion belongs in the mapping and not in the guards. Every guard compares
 * an exact role — deliberately — so making the roles hierarchical later cannot
 * silently widen one. This mirrors the API's own
 * `FederatedPlatformPrincipalResolver.ClientRoleToInternalRoles` exactly; the
 * two must be changed together or the console and the API will disagree about
 * who may do what.
 */
const CLIENT_ROLE_MAP: Record<string, readonly PlatformRoleValue[]> = {
  superadmin: [PlatformRole.SuperAdmin, PlatformRole.Admin],
  admin: [PlatformRole.Admin],
  auditor: [PlatformRole.Auditor],
};

interface TokenClaims {
  sub?: string;
  name?: string;
  preferred_username?: string;
  azp?: string;
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
}

/**
 * Federated implementation of {@link AccessProvider}, behind the seam
 * `src/access/types.ts` already defines — see that file and
 * `src/access/localAccessProvider.ts` for the contract this must honour.
 *
 * Token handling: the access token is **never** written to `localStorage`
 * or `sessionStorage`. `keycloak-js` already keeps it only in memory on the
 * client instance (`ssoProvider.token`); this module simply reads that
 * in-memory field on demand (`getSessionToken`) instead of copying it
 * anywhere else, which is the strongest available mitigation against
 * exfiltration by an injected script (see the wave report for the full
 * reasoning). This intentionally departs from the handbook's illustrative
 * `authSync.ts` (`docs/identity/README.md` §6.4), which persists to
 * `localStorage` — this repository's own hard constraints (see
 * `.claude/CLAUDE.md`, "Security") are stricter than that default and win.
 */
export function createFederatedAccessProvider(): AccessProvider {
  const authUrl = import.meta.env.VITE_AUTH_URL;
  const authRealm = import.meta.env.VITE_AUTH_REALM;
  const authClientId = import.meta.env.VITE_AUTH_CLIENT_ID;
  const portalUrl = import.meta.env.VITE_PORTAL_URL;
  const entitlementRole = import.meta.env.VITE_AUTH_ENTITLEMENT_ROLE;

  // Mirrors the local provider's own discipline (see localAccessProvider.ts):
  // an unconfigured build never throws at module-eval time — it degrades to
  // "no session", so the shell, branding and routing still render and land
  // on the designed no-session state instead of a blank page.
  if (!authUrl || !authRealm || !authClientId || !portalUrl || !entitlementRole) {
    return createUnconfiguredProvider();
  }
  // Rebound as freshly declared `string` consts (not the original `string |
  // undefined` bindings) so their narrowed type survives capture inside the
  // closures below — TypeScript's control-flow narrowing of the originals
  // does not itself propagate into a nested function literal's own scope.
  const portal: string = portalUrl;
  const clientId: string = authClientId;
  const entitlement: string = entitlementRole;

  let statePromise: Promise<AccessState> | null = null;
  // Single-shot terminal marker: once a close decision is made (inactivity
  // or a genuine renewal rejection), no later callback may navigate again.
  // This is what keeps a rebounding tab from looping (wave report, trap 2).
  let closed = false;
  const activity = createActivityTracker();
  const renewalCoordination = createRenewalCoordinator();
  let inactivityIntervalHandle: ReturnType<typeof setInterval> | undefined;

  // "Detect the session is unusable" and "navigate" are deliberately two
  // separate steps (wave report §2/§5, trap 1): every caller below decides
  // *that* the session must end; only this function decides *how* the
  // browser leaves. Never called from inside a renewal callback directly.
  function closeSession(): void {
    if (closed) return;
    closed = true;
    if (inactivityIntervalHandle !== undefined) clearInterval(inactivityIntervalHandle);
    window.location.href = `${portal}?app=${clientId}`;
  }

  function startInactivityWatch(): void {
    inactivityIntervalHandle = setInterval(() => {
      if (!closed && activity.isIdle(INACTIVITY_THRESHOLD_MS)) closeSession();
    }, INACTIVITY_CHECK_INTERVAL_MS);
  }

  async function attemptRenewal(allowRetry: boolean): Promise<void> {
    try {
      const refreshed = await ssoProvider.updateToken(30);
      if (refreshed) renewalCoordination.announceRenewed();
    } catch (error: unknown) {
      if (allowRetry) {
        // First failure is treated as a possible transient infrastructure
        // hiccup (network, CSP, the identity provider momentarily
        // unreachable) — never immediately as a session decision. This
        // mirrors the `unavailable` treatment init() failures already get
        // above, extended to the renewal path.
        await delay(TRANSIENT_RETRY_DELAY_MS);
        if (!closed) await attemptRenewal(false);
        return;
      }
      // Second consecutive failure: the refresh token itself is no longer
      // usable — a genuine rejection, not infrastructure. Close once.
      // eslint-disable-next-line no-console
      console.error('Session renewal failed:', error);
      closeSession();
    }
  }

  async function handleTokenExpired(): Promise<void> {
    if (closed) return;
    if (activity.isIdle(INACTIVITY_THRESHOLD_MS)) {
      // No point redeeming a refresh token nobody is using — close on our
      // own terms instead of waiting for the identity provider's absolute
      // session limit to do it for us.
      closeSession();
      return;
    }
    if (renewalCoordination.wasRenewedRecently()) {
      // A sibling tab just redeemed this same refresh token — attempting
      // our own renewal now would race a rotation and fail (trap 3).
      return;
    }
    await attemptRenewal(true);
  }

  function resolveState(): Promise<AccessState> {
    if (!statePromise) {
      statePromise = ssoProvider
        .init({
          onLoad: 'check-sso',
          pkceMethod: 'S256',
          silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        })
        .then((authenticated): AccessState => {
          if (!authenticated) {
            // No active corporate session anywhere — send the user to the
            // portal. Never window.open(): browsers block a non-user-gesture
            // popup, and the portal is meant to replace this tab, not spawn
            // one (identity policy §2.2 / §6.5).
            window.location.href = `${portal}?app=${clientId}`;
            return { kind: 'anonymous' };
          }
          ssoProvider.onTokenExpired = () => {
            void handleTokenExpired();
          };
          startInactivityWatch();
          return buildAuthorizedOrUnentitledState(entitlement);
        })
        .catch((error: unknown): AccessState => {
          // A rejection here is a configuration/infrastructure failure (a CSP
          // block, the identity provider unreachable, …) — never a session
          // decision, and therefore never the same fact as "no session"
          // (`anonymous`). Collapsing it into `anonymous` is what previously
          // made a pure defect look identical to an ordinary logged-out
          // visitor. No redirect here on purpose: a redirect loop on a
          // misconfiguration is worse than a clear, static message (see
          // `AccessUnavailablePage`). The error is logged for a developer —
          // `error` here is whatever keycloak-js's init() rejects with
          // (typically a plain Error/event describing the failed check, e.g.
          // an iframe timeout), never a token or credential.
          // eslint-disable-next-line no-console
          console.error('Access provider initialization failed:', error);
          return { kind: 'unavailable' };
        });
    }
    return statePromise;
  }

  return {
    async resolvePrincipal() {
      const state = await resolveState();
      return state.kind === 'authorized' ? state.principal : null;
    },

    async resolveAccessState() {
      return resolveState();
    },

    async getSessionToken() {
      const state = await resolveState();
      if (state.kind !== 'authorized') return null;
      // A real call to the platform API is the strongest available signal
      // that an operator is genuinely working, not just resting a cursor on
      // the tab — see the wave report, §4 "qué cuenta como actividad".
      activity.markActivity();
      return ssoProvider.token ?? null;
    },

    async endSession() {
      closed = true;
      if (inactivityIntervalHandle !== undefined) clearInterval(inactivityIntervalHandle);
      await ssoProvider.logout({ redirectUri: portal });
    },
  };
}

function buildAuthorizedOrUnentitledState(entitlementRole: string): AccessState {
  const claims = ssoProvider.tokenParsed as TokenClaims | undefined;
  const realmRoles = claims?.realm_access?.roles ?? [];

  if (!realmRoles.includes(entitlementRole)) {
    return { kind: 'unentitled' };
  }

  // Prefer the token's own client (azp) so a dev/prod alias client resolves
  // its own resource_access entry transparently (identity policy §3).
  const clientId = claims?.azp ?? ssoProvider.clientId;
  const clientRoles = (clientId ? claims?.resource_access?.[clientId]?.roles : undefined) ?? [];
  const roles = Array.from(
    new Set(clientRoles.flatMap((role): readonly PlatformRoleValue[] => CLIENT_ROLE_MAP[role] ?? [])),
  );

  const id = claims?.sub;
  if (!id) {
    return { kind: 'unentitled' };
  }

  const principal: Principal = {
    id,
    displayName: claims?.name ?? claims?.preferred_username ?? id,
    roles,
  };
  return { kind: 'authorized', principal };
}

function createUnconfiguredProvider(): AccessProvider {
  return {
    async resolvePrincipal() {
      return null;
    },
    async resolveAccessState() {
      return { kind: 'anonymous' };
    },
    async getSessionToken() {
      return null;
    },
    async endSession() {
      // Nothing to tear down: no session was ever established.
    },
  };
}
